import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { TooltipProvider } from "@/components/ui/tooltip";
import AppShell from "@/components/layout/AppShell";
import LoginPage from "@/pages/Login";
import DashboardPage from "@/pages/Dashboard";
import TaxToolPage from "@/pages/TaxTool";
import HostingToolPage from "@/pages/HostingTool";
import IspToolPage from "@/pages/IspTool";
import ScraperToolPage from "@/pages/ScraperTool";
import EqualizerToolPage from "@/pages/EqualizerTool";
import CreditCardToolPage from "@/pages/CreditCardTool";
import MeetingToolPage from "@/pages/MeetingTool";
import PublicVotePage from "@/pages/PublicVote";
import SavedPage from "@/pages/Saved";
import UsagePage from "@/pages/Usage";
import MonitorAiToolPage from "@/pages/MonitorAiTool";
import AdminPage from "@/pages/Admin";
import { Loader2 } from "lucide-react";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="app-loading">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
    </div>
  );
}

function Protected({ children, admin = false }) {
  const { user, loading, isAdmin } = useAuth();
  const loc = useLocation();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  if (admin && !isAdmin) return <Navigate to="/dashboard" replace />;
  return <AppShell>{children}</AppShell>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="App app-noise min-h-screen">
      <AuthProvider>
        <TooltipProvider delayDuration={200}>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
              <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
              <Route path="/tools/pajak" element={<Protected><TaxToolPage /></Protected>} />
              <Route path="/tools/hosting" element={<Protected><HostingToolPage /></Protected>} />
              <Route path="/tools/isp" element={<Protected><IspToolPage /></Protected>} />
              <Route path="/tools/ekualizer" element={<Protected admin><EqualizerToolPage /></Protected>} />
              <Route path="/tools/kartu-kredit" element={<Protected admin><CreditCardToolPage /></Protected>} />
              <Route path="/tools/meeting" element={<Protected admin><MeetingToolPage /></Protected>} />
              <Route path="/vote/:publicId" element={<PublicVotePage />} />
              <Route path="/tools/scraper" element={<Protected admin><ScraperToolPage /></Protected>} />
              <Route path="/tools/monitor-ai" element={<Protected admin><MonitorAiToolPage /></Protected>} />
              <Route path="/saved" element={<Protected><SavedPage /></Protected>} />
              <Route path="/usage" element={<Protected><UsagePage /></Protected>} />
              <Route path="/admin" element={<Protected admin><AdminPage /></Protected>} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster
            theme="dark"
            position="top-right"
            toastOptions={{
              classNames: {
                toast: "bg-[hsl(220_14%_8%)] border border-[hsl(43_35%_35%/0.45)] text-foreground",
                description: "text-muted-foreground",
              },
            }}
          />
        </TooltipProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
